import { userApi } from "../../api/userApi.js"
import { roleApi } from "../../api/roleApi.js"

import { addNotification, NotificationTypes } from "../actions/notificationActions.js"

import { ephemeralUserListSignal, ephemeralUserListLoadedSignal, ephemeralUserListErrorSignal, userListSignal, userListLoadedSignal, userListErrorSignal, userSignal, userLoadedSignal, userErrorSignal, userGroupsSignal, userGroupsErrorSignal, userGroupsLoadedSignal, userRolesSignal, userRolesLoadedSignal, userRolesErrorSignal, userPermissionsSignal, userPermissionsErrorSignal, userPermissionsLoadedSignal, currentUserSignal, currentUserLoadedSignal, currentUserErrorSignal, systemUserListSignal, systemUserListLoadedSignal, systemUserListErrorSignal } from "../signals/userSignals.js"

import { roleDictionarySignal } from '../signals/roleSignals.js'

import { fetchUserManifestByAccessToken } from "./userManifestActions.js"
import { businessEntityDictionarySignal } from "../signals/businessEntitySignals.js"

export async function fetchEphemeralUserList(businessEntityId, config, accessToken) {
    ephemeralUserListLoadedSignal.value = false
    ephemeralUserListSignal.value = []
    return await userApi.fetchEphemeralUserList(businessEntityId, config, accessToken).then(
        (ephemeralUsers) => {
            ephemeralUserListSignal.value = ephemeralUsers
            ephemeralUserListLoadedSignal.value = true
            return ephemeralUserListSignal.value
        }
    ).catch(
        (err) => {
            ephemeralUserListErrorSignal.value = err.message
            throw err
        }
    )
}

export async function fetchUserList(businessEntityId, config, accessToken, isSystemUser = false) {
    userListLoadedSignal.value = false
    userListSignal.value = []
    return await userApi.fetchUserList(businessEntityId, config, accessToken, isSystemUser).then(
        (users) => {
            userListSignal.value = users
            userListLoadedSignal.value = true
            return users
        }
    ).catch(
        (err) => {
            userListErrorSignal.value = err.message
            throw err
        }
    )
}

export async function deactivateUser(userId, config, accessToken) {
    userLoadedSignal.value = false
    return await userApi.deactivateUser(userId, config, accessToken).then(
        (deletedUser) => {
            userSignal.value = null
            return deletedUser
        }
    ).catch(
        (error) => {
            userErrorSignal.value = error
            throw error
        }
    )
}

export async function deleteUser(userId, config, accessToken) {
    userLoadedSignal.value = false
    return await userApi.deleteUser(userId, config, accessToken).then(
        (response) => {
            userSignal.value = null
            return response
        }
    ).catch(
        (error) => {
            userErrorSignal.value = error
            throw error
        }
    )
}

export async function fetchSystemUserList(businessEntityId, config, accessToken) {
    systemUserListLoadedSignal.value = false
    systemUserListSignal.value = []
    return await userApi.fetchUserList(businessEntityId, config, accessToken, true).then(
        (users) => {
            systemUserListSignal.value = users
            systemUserListLoadedSignal.value = true
            return users
        }
    ).catch(
        (err) => {
            systemUserListErrorSignal.value = err.message
            throw err
        }
    )
}

export async function fetchUser(userId, config, accessToken) {
    userLoadedSignal.value = false
    userSignal.value = null
    return await userApi.fetchUser(userId, config, accessToken).then(
        (user) => {
            userSignal.value = user
            userLoadedSignal.value = true
            return user
        }
    ).catch(
        (err) => {
            userErrorSignal.value = err.message
            throw err
        }
    )
}

export const fetchUserByAccessToken = async (config, accessToken) => {
    currentUserLoadedSignal.value = false
    currentUserSignal.value = null
    return await userApi.fetchUserByAccessToken(config, accessToken).then(
        async (user) => {
            currentUserSignal.value = user
            return await fetchUserManifestByAccessToken(config, accessToken).then(
                () => {
                    currentUserLoadedSignal.value = true
                    return currentUserSignal.value
                }
            )
        }
    ).catch(
        (err) => {
            currentUserErrorSignal.value = err
            console.log(err)
            addNotification(`Unable to fetch user by access token due to the following error - ${err}`)
        }
    )
}

export const addUser = async (businessEntityId, user, config, accessToken) => {
    userListLoadedSignal.value = false
    return await userApi.addUser(businessEntityId, user, config, accessToken).then(
        async (user) => {
            return await fetchUserList(businessEntityId, config, accessToken, false).then(
                () => {
                    return user
                }
            )
        }
    ).catch(
        (error) => {
            userListErrorSignal.value = error
            throw error
        }
    )
}

export const addUserRole = async (businessEntityId, userId, roleId, requestBody, config, accessToken) => {
    userRolesLoadedSignal.value = false
    return await userApi.addUserRole(businessEntityId, userId, roleId, requestBody, config, accessToken).then(
        async (userRole) => {
            return await fetchUserRoles(userId, config, accessToken).then(
                () => {
                    return userRole
                }
            )
        }
    ).catch(
        (error) => {
            userRolesErrorSignal.value = error
            throw error
        }
    )
}

export const deleteUserRole = async (userId, roleId, requestBody, config, accessToken) => {
    userRolesLoadedSignal.value = false
    userRolesSignal.value = []
    userRolesErrorSignal.value = null
    return await userApi.deleteUserRole(userId, roleId, requestBody, config, accessToken).then(
        async () => {
            return await fetchUserRoles(userId, config, accessToken).then(
                (userRoles) => {
                    userRolesSignal.value = userRoles
                    return userRolesSignal.value
                }
            )
        }
    ).catch(
        (error) => {
            userRolesErrorSignal.value = error
            throw error
        }
    )
}

export const createEphemeralUser = async (businessEntityId, config, ephemeralUser, accessToken) => {
    ephemeralUserListLoadedSignal.value = false
    return await userApi.createEphemeralUser(businessEntityId, config, ephemeralUser, accessToken).then(
        async (ephemeralUser) => {
            return await fetchEphemeralUserList(businessEntityId, config, accessToken).then(
                () => {
                    return ephemeralUser
                }
            )
        }
    ).catch(
        (error) => {
            ephemeralUserListErrorSignal.value = error
            throw error
        }
    )
}

export const updateUser = async (userId, user, config, accessToken) => {
    return await userApi.updateUser(userId, user, config, accessToken)
}

export const validateUsername = async (userId, userName, config, accessToken) => {
    let result
    try {
        result = await userApi.validateUsername(userId, userName, config, accessToken)
    } catch (err) {
        console.error(err)
        addNotification(`Unable to check if Username is available`, NotificationTypes.ERROR, false, 'Error')
    }
    return result
}

export const validateEmail = async (userId, email, config, accessToken) => {
    let result
    try {
        result = await userApi.validateEmail(userId, email, config, accessToken)
    } catch (err) {
        console.error(err)
        addNotification(`Unable to check if email is available`, NotificationTypes.ERROR, false, 'Error')
    }
    return result
}

export const fetchUserGroups = async (userId, config, accessToken, generateRoleDictionary = false) => {
    const f = `fetchUserGroups()`
    let print = false
    if (print) {
        console.log(`${f}: entered`)
    }
    userGroupsLoadedSignal.value = false
    userGroupsSignal.value = []
    if (generateRoleDictionary) {
        roleDictionarySignal.value = null
    }
    return await userApi.fetchUserGroups(userId, config, accessToken).then(
        async (userGroups) => {
            userGroupsSignal.value = userGroups
            /* Generates a dictionary of roles, needed for user roles page */
            if (generateRoleDictionary) {
                if (print) {
                    console.log("Generating role dictionary")
                }
                let dictionary = {}
                let roleIds = []
                for (let i = 0; i < userGroups.length; i++) {
                    const group = userGroups[i]
                    if (!('roleIds' in group) || group.roleIds.length == 0) {
                        continue
                    }
                    for (let j = 0; j < group.roleIds.length; j++) {
                        const roleId = group.roleIds[j]
                        if (roleId in dictionary) {
                            continue
                        }
                        dictionary[roleId] = null
                        roleIds.push(roleId)
                    }
                }
                if (print) {
                    console.log("Role IDs to fetch:")
                    console.log(roleIds.toString())
                }
                const fetchRole = async (roleId) => {
                    return new Promise(
                        (resolve, reject) => {
                            roleApi.fetchRole(roleId, config, accessToken).then(
                                (role) => {
                                    if (print) {
                                        console.log(`Fetched role ${roleId}`)
                                    }
                                    dictionary[roleId] = role
                                    resolve(roleId)
                                }
                            ).catch(
                                (x) => {
                                    reject(x)
                                }
                            )
                        }
                    )
                }
                const promises = roleIds.map(roleId => fetchRole(roleId))
                return await Promise.all(promises).then(
                    () => {
                        if (print) {
                            console.log("Successfully fetched all roles")
                        }
                        roleDictionarySignal.value = dictionary
                        userGroupsLoadedSignal.value = true
                        return userGroups
                    }
                ).catch(
                    (x) => {
                        throw new Error("Error resolving promises to fetch roles: " + x.toString())
                    }
                )
            } else if (print) {
                console.log("Not generating a role dictionary this time")
            }
            userGroupsLoadedSignal.value = true
            return userGroups
        }
    ).catch(
        (error) => {
            userGroupsErrorSignal.value = error
            throw error
        }
    )
}

export const addUserToGroup = async (userId, requestBody, config, accessToken) => {
    userGroupsLoadedSignal.value = false
    return await userApi.addUserToGroup(userId, requestBody, config, accessToken).then(
        async (userGroups) => {
            return await fetchUserGroups(userId, config, accessToken).then(
                () => {
                    return userGroups
                }
            )
        }).catch(
            (error) => {
                userGroupsErrorSignal.value = error
                throw error
            }
        )
}

export const removeUserFromGroup = async (userId, groupId, config, accessToken) => {
    userGroupsLoadedSignal.value = false
    return await userApi.removeUserFromGroup(userId, groupId, config, accessToken).then(
        async (response) => {
            console.log("User removed from group successfully: ", response)
            return await fetchUserGroups(userId, config, accessToken).then(
                (userGroups) => {
                    return userGroups
                }
            )
        }).catch(
            (error) => {
                userGroupsErrorSignal.value = error
                throw error
            }
        )
}

export const fetchUserRoles = async (userId, config, accessToken) => {
    userRolesLoadedSignal.value = false
    userRolesErrorSignal.value = null
    userRolesSignal.value = []
    try {
        const roleBusinessEntities = await userApi.fetchUserRoles(userId, config, accessToken)
        if (!Array.isArray(roleBusinessEntities) || roleBusinessEntities.length === 0) {
            userRolesSignal.value = []
            userRolesLoadedSignal.value = true
            return []
        }
        const uniqueRoleIds = [...new Set(roleBusinessEntities.map(x => x?.roleId).filter(Boolean))]
        const roles = await Promise.all(
            uniqueRoleIds.map(roleId => roleApi.fetchRole(roleId, config, accessToken))
        )
        const roleNameById = new Map(
            roles
                .filter(Boolean)
                .map(r => [r.id ?? r.roleId, r.name])
        )
        const enriched = roleBusinessEntities.map(rbe => ({
            ...rbe,
            roleName: roleNameById.get(rbe.roleId) ?? null,
        }))
        userRolesSignal.value = enriched
        userRolesLoadedSignal.value = true
        return enriched
    } catch (error) {
        userRolesErrorSignal.value = error
        userRolesLoadedSignal.value = true
        throw error
    }
}


export const fetchUserPermissions = async (userId, config, accessToken) => {
    userPermissionsLoadedSignal.value = false
    userPermissionsSignal.value = []
    return await userApi.fetchUserPermissions(userId, config, accessToken).then(
        (permissions) => {
            userPermissionsSignal.value = permissions
            userPermissionsLoadedSignal.value = true
            return permissions
        }
    ).catch(
        (error) => {
            userPermissionsErrorSignal.value = error
            throw error
        }
    )
}